import { useEffect, useMemo, useState } from 'react'
import { CalendarClock } from 'lucide-react'
import {
  addMinutesToDateTimeLocal,
  dateTimeLocalDeltaMinutes,
  formatDateTime,
  fromDateTimeLocalInput,
  OPERATIONS_TIME_ZONE_LABEL,
  toDateTimeLocalInput,
} from '@/lib/datetime'
import { queryKeys } from '@/lib/query-client'
import type { RescheduleBookingRequest } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { rescheduleBooking } from '@/features/trip-requests/api'
import { rescheduleTrip } from '@/features/trips/api'
import styles from './EditScheduleDialog.module.css'

/**
 * The booking being re-timed, as either screen knows it.
 *
 * `kind` decides which endpoint is called, and nothing else: both move the
 * booking and its trip together, so the outcome is identical. The trips screen
 * has a trip id and the approval queue has a booking id, and neither should
 * have to look up the other's.
 */
export interface ScheduleTarget {
  kind: 'trip' | 'request'
  id: number
  reference: string | null
  customerName?: string | null
  startTime: string | null
  endTime: string | null
}

interface EditScheduleDialogProps {
  /** The booking to re-time. Null closes the dialog. */
  target: ScheduleTarget | null
  onClose: () => void
}

const INVALIDATIONS = [
  queryKeys.trips.all,
  queryKeys.tripRequests.all,
  queryKeys.changeLog.all,
  queryKeys.dashboard.all,
]

/**
 * Moves a booking's pickup and finish times.
 *
 * The customer rings the office to change their trip, and until now there was
 * nowhere to do it: a pending request could only be approved or rejected, so it
 * had to be cancelled and re-booked — losing the reference, the promo and, on a
 * card booking, the payment.
 *
 * Moving the start drags the end with it, keeping the booked duration intact,
 * because a package is sold as a number of hours. The operator can still edit
 * the end on its own; once they have, it stops following.
 */
export function EditScheduleDialog({ target, onClose }: EditScheduleDialogProps) {
  const open = target !== null

  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [endEdited, setEndEdited] = useState(false)
  const [reason, setReason] = useState('')

  const bookedStart = toDateTimeLocalInput(target?.startTime)
  const bookedEnd = toDateTimeLocalInput(target?.endTime)

  useEffect(() => {
    if (!target) return
    setStartTime(toDateTimeLocalInput(target.startTime))
    setEndTime(toDateTimeLocalInput(target.endTime))
    setEndEdited(false)
    setReason('')
  }, [target])

  /**
   * Keeps the booked duration when the pickup moves. Skipped once the operator
   * has set an end time themselves — at that point they are describing a
   * different trip, not the same one an hour later.
   */
  const handleStartChange = (value: string) => {
    setStartTime(value)
    if (endEdited || !bookedStart || !endTime) return

    const shift = dateTimeLocalDeltaMinutes(bookedStart, value)
    if (shift === null || shift === 0 || !bookedEnd) return
    setEndTime(addMinutesToDateTimeLocal(bookedEnd, shift))
  }

  const startChanged = startTime !== '' && startTime !== bookedStart
  const endChanged = endTime !== '' && endTime !== bookedEnd
  const nothingChanged = !startChanged && !endChanged

  const orderError = useMemo(() => {
    if (!startTime || !endTime) return undefined
    const span = dateTimeLocalDeltaMinutes(startTime, endTime)
    return span !== null && span <= 0 ? 'The end time must be after the start time.' : undefined
  }, [startTime, endTime])

  const mutation = useAdminMutation({
    mutationFn: () => {
      // Only what actually moved is sent. An unchanged field would otherwise be
      // recorded in the booking's history as a change that changed nothing.
      const change: RescheduleBookingRequest = {
        startTime: startChanged ? fromDateTimeLocalInput(startTime) : undefined,
        endTime: endChanged ? fromDateTimeLocalInput(endTime) : undefined,
        reason: reason.trim() || undefined,
      }
      const id = target?.id as number
      return target?.kind === 'trip' ? rescheduleTrip(id, change) : rescheduleBooking(id, change)
    },
    successTitle: 'Trip times updated',
    errorTitle: 'The times were not changed',
    invalidate: INVALIDATIONS,
    onSuccess: () => onClose(),
  })

  const canSubmit = !nothingChanged && !orderError && !mutation.isPending

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Change the trip times"
      size="md"
      busy={mutation.isPending}
      description={
        target
          ? `Booking ${target.reference ?? `#${target.id}`}${
              target.customerName ? ` for ${target.customerName}` : ''
            }. The passenger and the assigned driver are both notified.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            iconLeft={<CalendarClock aria-hidden="true" />}
            loading={mutation.isPending}
            disabled={!canSubmit}
            onClick={() => mutation.mutate(undefined)}
          >
            {mutation.isPending ? 'Saving…' : 'Save times'}
          </Button>
        </>
      }
    >
      {target ? (
        <div className={styles.body}>
          <p className={styles.note}>
            Entered and stored as {OPERATIONS_TIME_ZONE_LABEL} local time, matching the driver and passenger
            apps.
          </p>

          <Field
            label="Start time"
            hint={`Currently ${formatDateTime(target.startTime)}.`}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="datetime-local"
                value={startTime}
                disabled={mutation.isPending}
                onChange={(event) => handleStartChange(event.target.value)}
              />
            )}
          </Field>

          <Field
            label="End time"
            error={orderError}
            hint={
              target.endTime
                ? `Currently ${formatDateTime(target.endTime)}. Moves with the start time unless you set it yourself.`
                : 'Not set — this booking is open-ended.'
            }
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="datetime-local"
                value={endTime}
                disabled={mutation.isPending}
                onChange={(event) => {
                  setEndEdited(true)
                  setEndTime(event.target.value)
                }}
              />
            )}
          </Field>

          <Field
            label="Reason"
            hint="Optional, and recorded against the change — “customer called to move it to the afternoon” is what answers the complaint six weeks later."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={reason}
                maxLength={500}
                disabled={mutation.isPending}
                placeholder="Customer called to move the pickup…"
                onChange={(event) => setReason(event.target.value)}
              />
            )}
          </Field>

          {nothingChanged ? (
            <p className={styles.note}>These are the times already on the booking.</p>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  )
}
